import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";

const painPoints = [
  "保單很多，卻不知道真正保障什麼",
  "重複保障太多，保費壓力越來越高",
  "醫療、重大傷病、長照缺口不清楚",
  "家庭責任改變，但保單沒有同步調整",
  "理賠文件與流程不熟，真正需要時很慌",
  "買了很多年，卻不知道是否符合現在需求",
];

const coreServices = [
  {
    number: "01",
    title: "保單健診",
    desc: "彙整既有保單，建立保障地圖與缺口清單。",
    icon: "ri-file-search-line",
  },
  {
    number: "02",
    title: "保單整合",
    desc: "保留需要的、刪除重複的、補足不足的，提升保費使用效率。",
    icon: "ri-stack-line",
  },
  {
    number: "03",
    title: "缺口補強",
    desc: "針對醫療、意外、重大傷病、壽險、長照等風險，提出優先順序。",
    icon: "ri-shield-star-line",
  },
];

const steps = [
  {
    number: "01",
    title: "初談釐清",
    desc: "了解家庭結構、責任、預算與擔心的風險。",
  },
  {
    number: "02",
    title: "健診盤點",
    desc: "彙整保單，做風險地圖與缺口清單。",
  },
  {
    number: "03",
    title: "整合優化",
    desc: "提出調整建議與保費試算。",
  },
  {
    number: "04",
    title: "追蹤調整",
    desc: "每季或半年回顧，重大事件即時微調。",
  },
];

export default function Policy() {
  return (
    <div className="min-h-screen bg-brand-black">
      <SEOHead
        title="保單門神 | 保單健診與整合服務 | 保險門神"
        description="保單門神專注於保單健診、整合與補強。痛點一次解決：重複保障、缺口、觀念不清。用數據把關你的保障，讓每一分保費花在真正需要的地方。"
        keywords="保單健診,保單整合,保障缺口,保費優化,保險規劃,保險門神"
        canonicalPath="/services/policy"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "保單門神 - 保單健診與整合服務",
          "description": "保單健診、整合與補強服務，彙整既有保單建立保障地圖與缺口清單，保留需要的、刪除重複的、補足不足的。",
          "provider": {
            "@type": "FinancialService",
            "name": "保險門神 Insurance Guardian",
            "url": "__SITE_URL__/"
          },
          "url": "__SITE_URL__/services/policy",
          "areaServed": { "@type": "Country", "name": "Taiwan" },
          "serviceType": "保險保單健診與整合"
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20shield%20and%20protection%20motifs%2C%20flowing%20gold%20metallic%20lines%20forming%20shield%20shapes%20on%20deep%20black%20canvas%2C%20subtle%20geometric%20grid%20pattern%2C%20premium%20insurance%20brand%20aesthetic%2C%20cinematic%20lighting%20with%20warm%20gold%20accents%2C%20sophisticated%20minimalist%20composition%2C%204K%20quality&width=1920&height=600&seq=102&orientation=landscape"
              alt="保單門神 Hero 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          <div className="container-main relative z-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[0.9375rem] mb-6">
              <Link to="/" className="text-brand-text-secondary hover:text-brand-gold transition-colors">
                首頁
              </Link>
              <i className="ri-arrow-right-s-line text-brand-text-secondary" />
              <Link to="/services" className="text-brand-text-secondary hover:text-brand-gold transition-colors">
                服務項目
              </Link>
              <i className="ri-arrow-right-s-line text-brand-text-secondary" />
              <span className="text-brand-gold">保單門神</span>
            </div>

            <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-4">
              保單門神
            </h1>
            <p className="text-brand-gold text-[1.1875rem] font-medium mb-4">
              保單健診・整合・補強
            </p>
            <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] max-w-[800px]">
              痛點一次解決：重複保障、缺口、觀念不清。用數據把關你的保障，讓每一分保費花在真正需要的地方。
            </p>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  常見痛點
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {painPoints.map((point, index) => (
                <ScrollReveal key={index} delay={index * 80}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-start gap-4">
                      <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold font-bold text-[0.9375rem] md:text-[1rem] shrink-0">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.7] pt-1">
                        {point}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Core Services Banner */}
        <section className="relative h-[260px] md:h-[340px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20insurance%20policy%20documents%20and%20protective%20shield%20motifs%20floating%20on%20deep%20black%20canvas%2C%20flowing%20gold%20metallic%20light%20highlighting%20document%20edges%2C%20subtle%20geometric%20grid%20pattern%2C%20premium%20financial%20security%20brand%20aesthetic%2C%20cinematic%20lighting%20with%20warm%20gold%20accents%2C%20sophisticated%20minimalist%20composition%2C%204K%20quality&width=1920&height=400&seq=201&orientation=landscape"
            alt="保單核心服務意象"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/70" />
        </section>

        {/* Core Services */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  三大核心服務
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {coreServices.map((service, index) => (
                <ScrollReveal key={service.number} delay={index * 120}>
                  <div
                    className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-gold/10">
                        <span className="w-6 h-6 flex items-center justify-center text-brand-gold text-xl">
                          <i className={service.icon} />
                        </span>
                      </div>
                      <span className="text-brand-gold font-bold text-[1rem] md:text-[1.125rem]">
                        {service.number}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.625rem] leading-[1.45] mb-3">
                      {service.title}
                    </h3>
                    <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.1875rem] leading-[1.75]">
                      {service.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  合作流程
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {steps.map((step, index) => (
                <ScrollReveal key={step.number} delay={index * 100}>
                  <div
                    className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:border-brand-gold/50 h-full"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-gold/20 border border-brand-gold flex items-center justify-center shrink-0">
                        <span className="text-brand-gold font-bold text-[0.9375rem] md:text-[1.0625rem]">
                          {step.number}
                        </span>
                      </div>
                      <h3 className="text-white font-semibold text-[1.125rem] md:text-[1.25rem]">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-brand-text-secondary text-[1.0625rem] leading-[1.75]">
                      {step.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-16 md:py-20">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  真實案例與成果
                </h2>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  client: "35 歲雙薪家庭",
                  pain: "夫妻各自投保 8 張保單，年繳保費 38 萬，卻不清楚保障內容。",
                  solution: "逐張盤點後發現 3 張重複險種、2 張保額不足。整合為 5 張精簡保單。",
                  result: "年繳保費降至 26 萬，醫療與重大傷病缺口補齊，保費效率提升 32%。",
                  tag: "保單整合",
                },
                {
                  client: "50 歲企業主管",
                  pain: "名下 12 張保單多為儲蓄險，醫療與長照保障幾乎為零。",
                  solution: "保留高 CP 值儲蓄險，新增實支醫療、重大傷病、長照險。",
                  result: "保費僅增加 8%，但保障覆蓋率從 45% 提升至 92%，退休風險大幅下降。",
                  tag: "缺口補強",
                },
                {
                  client: "新婚小家庭",
                  pain: "兩人保單皆為單身時期規劃，未涵蓋房貸與育兒責任。",
                  solution: "以家庭責任重新計算壽險額度，調整受益人與給付方式。",
                  result: "房貸與育兒支出完全覆蓋，家庭經濟安全網完整建立，保費零增加。",
                  tag: "責任調整",
                },
              ].map((c, index) => (
                <ScrollReveal key={c.tag} delay={index * 120}>
                  <div className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-8 transition-all duration-300 hover:border-brand-gold/50 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="inline-block border border-brand-gold/40 text-brand-gold text-[0.875rem] font-medium px-3 py-1 rounded-full">
                        {c.tag}
                      </span>
                    </div>
                    <p className="text-white font-semibold text-[1.125rem] md:text-[1.25rem] mb-4">
                      {c.client}
                    </p>

                    <div className="space-y-4 mb-6 flex-1">
                      <div>
                        <p className="text-brand-gold/80 text-[0.9375rem] font-medium mb-1">面臨問題</p>
                        <p className="text-brand-text-secondary text-[1rem] md:text-[1.0625rem] leading-[1.7]">
                          {c.pain}
                        </p>
                      </div>
                      <div>
                        <p className="text-brand-gold/80 text-[0.9375rem] font-medium mb-1">解決方案</p>
                        <p className="text-brand-text-secondary text-[1rem] md:text-[1.0625rem] leading-[1.7]">
                          {c.solution}
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-brand-border pt-5 mt-auto">
                      <p className="text-brand-gold font-bold text-[1.0625rem] md:text-[1.125rem] leading-[1.7]">
                        {c.result}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Quick Q&A */}
        <section className="py-16 md:py-20 bg-brand-card border-y border-brand-border">
          <div className="container-main">
            <ScrollReveal>
              <div className="flex items-center gap-3 mb-10 md:mb-12">
                <div className="w-1 h-8 bg-brand-gold rounded-full" />
                <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4]">
                  常見問題
                </h2>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="max-w-[800px] space-y-4">
                {[
                  {
                    q: "保單健診需要帶什麼資料？",
                    a: "只需要帶上你的保單正本或電子保單，以及近一年的保費繳納紀錄即可。我們會協助你逐張整理。",
                  },
                  {
                    q: "健診後一定會建議買新保險嗎？",
                    a: "不一定。我們會先分析現有保單的保障是否足夠，許多情況下只需調整保額或附加條款，不需要額外購買。",
                  },
                  {
                    q: "多久需要做一次保單健診？",
                    a: "建議至少每兩年做一次全面健診。若家庭結構、收入或健康狀況有重大變化，則應即時檢視。",
                  },
                  {
                    q: "舊保單已經停售，還能調整嗎？",
                    a: "停售保單通常仍可透過加保附加險、調整保額或搭配新保單補強缺口，我們會提供最佳整合建議。",
                  },
                ].map((faq, i) => (
                  <details
                    key={i}
                    className="bg-brand-black border border-brand-border rounded-xl group transition-all duration-300 hover:border-brand-gold/40"
                  >
                    <summary className="flex items-start justify-between gap-4 p-5 md:p-6 text-left cursor-pointer list-none">
                      <div className="flex items-start gap-3 md:gap-4">
                        <span className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold font-bold text-[0.875rem] md:text-[1rem] shrink-0 mt-0.5">
                          Q{i + 1}
                        </span>
                        <span className="text-white text-[1.0625rem] md:text-[1.1875rem] font-medium leading-[1.55] pt-1">
                          {faq.q}
                        </span>
                      </div>
                      <span className="w-6 h-6 flex items-center justify-center text-brand-gold shrink-0 mt-1 group-open:rotate-180 transition-transform duration-300">
                        <i className="ri-arrow-down-s-line text-xl" />
                      </span>
                    </summary>
                    <div className="px-5 md:px-6 pb-5 md:pb-6 pl-16 md:pl-[4.25rem]">
                      <p className="text-brand-text-secondary text-[1.0625rem] md:text-[1.125rem] leading-[1.75]">
                        {faq.a}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20shield%20radiating%20protective%20golden%20light%20beams%20on%20deep%20black%20canvas%2C%20subtle%20geometric%20protection%20grid%20expanding%20outward%2C%20premium%20insurance%20guardian%20brand%20aesthetic%2C%20powerful%20and%20reassuring%20atmosphere%20with%20warm%20gold%20accents%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=500&seq=209&orientation=landscape"
              alt="保單門神 CTA 背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/80" />
          </div>
          <div className="container-main relative z-10">
            <ScrollReveal className="text-center max-w-[700px] mx-auto">
              <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4] mb-3">
                門神在，你安心
              </h2>
              <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] mb-8">
                現在就預約免費健診，讓我們用數據把關你的保障。
              </p>
              <a
                href="https://lin.ee/o51JBHX"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-10 py-4 rounded-full text-[1.125rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
              >
                <i className="ri-line-fill" />
                加入 LINE 預約健診
              </a>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}